import React, { useState, useEffect } from 'react';
import { User, Mail, Lock, AlertCircle, Building2, FileText, ArrowLeft, Globe } from 'lucide-react';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import type { ContactInfo, UserType } from '../types';
import { useAuthStore } from '../store/authStore';
import { Footer } from './Footer';

interface ContactFormProps {
  onSubmit: (data: ContactInfo & { password: string; accountType: string; websiteUrl?: string }) => void;
  onBack?: () => void;
  initialData?: ContactInfo & { password: string; accountType: string; websiteUrl?: string };
  userType?: UserType | null;
}

export function ContactForm({ onSubmit, onBack, initialData, userType }: ContactFormProps) {
  const [formData, setFormData] = useState<Omit<ContactInfo, 'verified'> & { password: string; companyName: string; crNumber: string; accountType: string; websiteUrl?: string }>({
    name: initialData?.name || '',
    email: initialData?.email || '',
    phone: initialData?.phone || '',
    password: initialData?.password || '',
    companyName: initialData?.companyName || '',
    crNumber: initialData?.crNumber || '',
    accountType: initialData?.accountType || (userType === 'industrial' ? 'corporate' : 'individual'),
    websiteUrl: initialData?.websiteUrl || ''
  });
  
  
  const { error, isLoading, clearError } = useAuthStore();

  // Update form data when userType changes
  useEffect(() => {
    if (userType === 'industrial') {
      setFormData(prev => ({
        ...prev,
        accountType: 'corporate'
      }));
    }
  }, [userType]);


  // Validation functions
  const validateEmail = (email: string) => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return re.test(email);
  };

  const validatePhone = (phone: string) => {
    // E.164 format validation: + followed by up to 15 digits (ignore spaces/dashes from the picker UI)
    const compact = phone.replace(/[\s-]/g, '');
    const re = /^\+[1-9]\d{1,14}$/;
    return re.test(compact);
  };

  const validatePassword = (password: string) => {
    return password.length >= 8;
  };

  const validateWebsiteUrl = (url: string) => {
    if (!url.trim()) return true; // Optional field
    
    // More relaxed validation - accepts various formats
    const patterns = [
      /^https?:\/\/.+\..+/, // https://example.com
      /^www\..+\..+/, // www.example.com
      /^[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.[a-zA-Z]{2,}$/, // example.com
      /^[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.[a-zA-Z]{2,}\/.*$/, // example.com/path
    ];
    
    return patterns.some(pattern => pattern.test(url.trim()));
  };

  // Validation state
  const [validationErrors, setValidationErrors] = useState<{
    name?: string;
    email?: string;
    phone?: string;
    password?: string;
    companyName?: string;
    crNumber?: string;
    websiteUrl?: string;
  }>({});

  // Real-time validation
  const validateField = (field: string, value: string) => {
    let error = '';
    
    switch (field) {
      case 'name':
        if (!value.trim()) {
          error = 'Name is required';
        }
        break;
      case 'email':
        if (!value.trim()) {
          error = 'Email is required';
        } else if (!validateEmail(value)) {
          error = 'Please enter a valid email address';
        }
        break;
      case 'phone':
        if (!value.trim()) {
          error = 'Phone number is required';
        } else if (!validatePhone(value)) {
          error = 'Please enter a valid phone number';
        }
        break;
      case 'password':
        if (!value) {
          error = 'Password is required';
        } else if (!validatePassword(value)) {
          error = 'Password must be at least 8 characters long';
        }
        break;
      case 'companyName':
        if (formData.accountType === 'corporate' && !value.trim()) {
          error = 'Company name is required for corporate accounts';
        }
        break;
      case 'crNumber':
        // CR number is optional - no validation required
        break;
      case 'websiteUrl':
        if (userType === 'industrial' || formData.accountType === 'corporate') {
          if (!value.trim()) {
            error = 'Website URL is required for corporate accounts';
          } else if (!validateWebsiteUrl(value)) {
            error = 'Please enter a valid website URL (e.g., example.com, www.example.com, or https://example.com)';
          }
        } else if (value.trim() && !validateWebsiteUrl(value)) {
          error = 'Please enter a valid website URL (e.g., example.com, www.example.com, or https://example.com)';
        }
        break;
    }
    
    setValidationErrors(prev => ({
      ...prev,
      [field]: error
    }));
    
    return !error;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    
    // Clear previous validation errors
    setValidationErrors({});
    
    // Validate all fields
    const isNameValid = validateField('name', formData.name);
    const isEmailValid = validateField('email', formData.email);
    const isPhoneValid = validateField('phone', formData.phone);
    const isPasswordValid = validateField('password', formData.password);
    const isCompanyNameValid = validateField('companyName', formData.companyName);
    const isCrNumberValid = validateField('crNumber', formData.crNumber);
    const isWebsiteUrlValid = validateField('websiteUrl', formData.websiteUrl || '');
    
    // Check if all validations pass
    if (!isNameValid || !isEmailValid || !isPhoneValid || !isPasswordValid || 
        !isCompanyNameValid || !isCrNumberValid || !isWebsiteUrlValid) {
      return;
    }
    
    try {
      const phoneForSubmit = formData.phone.replace(/[\s-]/g, '');
      await onSubmit({ 
        ...formData,
        phone: phoneForSubmit,
        verified: false
      });
    } catch (err) {
      console.error('Error in form submission:', err);
      // Error handling is done in the store
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      {onBack && (
        <div className="mb-6">
          <button
            onClick={onBack}
            className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back
          </button>
        </div>
      )}
      
      <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-200">
        <h2 className="text-2xl font-bold mb-6 text-gray-800">Contact Information</h2>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Account Type Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Account Type
            </label>
            <div className="grid grid-cols-2 gap-4">
              <div className="relative">
              <button
                type="button"
                  disabled={userType === 'industrial'}
                  className={`p-4 rounded-lg border-2 transition-all w-full ${
                  formData.accountType === 'individual'
                    ? 'border-blue-500 bg-blue-50'
                      : userType === 'industrial'
                      ? 'border-gray-200 bg-gray-100 cursor-not-allowed opacity-50'
                    : 'border-gray-200 hover:border-blue-200'
                }`}
                  onClick={() => userType !== 'industrial' && setFormData({...formData, accountType: 'individual'})}
                  title={userType === 'industrial' ? 'Industrial projects require corporate registration' : ''}
              >
                <div className="flex items-center">
                  <User className={`h-5 w-5 ${
                    formData.accountType === 'individual' ? 'text-blue-500' : 'text-gray-500'
                  } mr-2`} />
                  <span className="font-medium">Individual</span>
                </div>
              </button>
                {userType === 'industrial' && (
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                    <div className="bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                      Industrial projects require corporate registration
                    </div>
                  </div>
                )}
              </div>
              
              <button
                type="button"
                className={`p-4 rounded-lg border-2 transition-all ${
                  formData.accountType === 'corporate'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-blue-200'
                }`}
                onClick={() => setFormData({...formData, accountType: 'corporate'})}
              >
                <div className="flex items-center">
                  <Building2 className={`h-5 w-5 ${
                    formData.accountType === 'corporate' ? 'text-blue-500' : 'text-gray-500'
                  } mr-2`} />
                  <span className="font-medium">Corporate</span>
                </div>
              </button>
            </div>
          </div>

          {/* Single Column Layout for Industrial Users */}
          {userType === 'industrial' ? (
            <div className="space-y-6">
              {/* Personal Contact Section */}
              <div className="border-b border-gray-200 pb-2 mb-6">
                <h3 className="text-lg font-medium text-gray-900">Personal Contact</h3>
                <p className="text-sm text-gray-600">Your personal information for account access</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Full Name
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <User className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                required
                className={`block w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  validationErrors.name ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter your full name"
                value={formData.name}
                onChange={(e) => {
                  setFormData({ ...formData, name: e.target.value });
                  validateField('name', e.target.value);
                }}
                disabled={isLoading}
              />
              {validationErrors.name && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {validationErrors.name}
                </p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="email"
                required
                className={`block w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  validationErrors.email ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter your email"
                value={formData.email}
                onChange={(e) => {
                  setFormData({ ...formData, email: e.target.value });
                  validateField('email', e.target.value);
                }}
                disabled={isLoading}
              />
              {validationErrors.email && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {validationErrors.email}
                </p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number
            </label>
            <div className="relative">
              <PhoneInput
                defaultCountry="sa"
                value={formData.phone}
                onChange={(phone) => {
                  setFormData({ ...formData, phone });
                  validateField('phone', phone);
                }}
                disabled={isLoading}
                className={`phone-input ${validationErrors.phone ? 'phone-input-error' : ''}`}
                inputStyle={{
                  width: '100%',
                  padding: '8px 12px',
                  border: validationErrors.phone ? '1px solid #ef4444' : '1px solid #d1d5db',
                  borderRadius: '0 8px 8px 0',
                  fontSize: '16px',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                  marginLeft: '4px',
                }}
                countrySelectorStyleProps={{
                  buttonStyle: {
                    padding: '8px 12px',
                    border: validationErrors.phone ? '1px solid #ef4444' : '1px solid #d1d5db',
                    borderRadius: '8px 0 0 8px',
                    backgroundColor: '#f9fafb',
                    fontSize: '16px',
                    marginRight: '4px',
                  }
                }}
              />
              {validationErrors.phone && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {validationErrors.phone}
                </p>
              )}
            </div>
          </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
              <input
                type="password"
                required
                minLength={8}
                title="Password must be at least 8 characters long"
                className={`block w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  validationErrors.password ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Create a password"
                value={formData.password}
                onChange={(e) => {
                  setFormData({ ...formData, password: e.target.value });
                  validateField('password', e.target.value);
                }}
                disabled={isLoading}
              />
              {validationErrors.password && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {validationErrors.password}
                </p>
              )}
                </div>
              </div>

              {/* Company Information Section - Now under Personal Contact */}
              <div className="border-b border-gray-200 pb-2 mb-6 mt-8">
                <h3 className="text-lg font-medium text-gray-900">Company Information</h3>
                <p className="text-sm text-gray-600">Details about your organization</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Name
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Building2 className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    className={`block w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      validationErrors.companyName ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter company name"
                    value={formData.companyName}
                    onChange={(e) => {
                      setFormData({ ...formData, companyName: e.target.value });
                      validateField('companyName', e.target.value);
                    }}
                    disabled={isLoading}
                  />
                  {validationErrors.companyName && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {validationErrors.companyName}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Website URL
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Globe className="h-5 w-5 text-gray-400" />
                  </div>
              <input
                type="url"
                required
                className={`block w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  validationErrors.websiteUrl ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="example.com or https://www.yourcompany.com"
                value={formData.websiteUrl || ''}
                onChange={(e) => {
                  setFormData({ ...formData, websiteUrl: e.target.value });
                  validateField('websiteUrl', e.target.value);
                }}
                disabled={isLoading}
              />
              {validationErrors.websiteUrl && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {validationErrors.websiteUrl}
                </p>
              )}
                </div>
              </div>

              {/* CR Number for Industrial Users */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  CR Number
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Building2 className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    className={`block w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      validationErrors.crNumber ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter CR number"
                    value={formData.crNumber}
                    onChange={(e) => {
                      setFormData({ ...formData, crNumber: e.target.value });
                      validateField('crNumber', e.target.value);
                    }}
                    disabled={isLoading}
                  />
                  {validationErrors.crNumber && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {validationErrors.crNumber}
                    </p>
                  )}
                </div>
              </div>

            </div>
          ) : (
            /* Single Column Layout for Other User Types */
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    className={`block w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      validationErrors.name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter your full name"
                    value={formData.name}
                    onChange={(e) => {
                      setFormData({ ...formData, name: e.target.value });
                      validateField('name', e.target.value);
                    }}
                    disabled={isLoading}
                  />
                  {validationErrors.name && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {validationErrors.name}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="email"
                    className={`block w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      validationErrors.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={(e) => {
                      setFormData({ ...formData, email: e.target.value });
                      validateField('email', e.target.value);
                    }}
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <div className="relative">
                  <PhoneInput
                    defaultCountry="sa"
                    value={formData.phone}
                    onChange={(phone) => {
                      setFormData({ ...formData, phone });
                      validateField('phone', phone);
                    }}
                    disabled={isLoading}
                    className={`phone-input ${validationErrors.phone ? 'phone-input-error' : ''}`}
                    inputStyle={{
                      width: '100%',
                      padding: '8px 12px',
                      border: validationErrors.phone ? '1px solid #ef4444' : '1px solid #d1d5db',
                      borderRadius: '0 8px 8px 0',
                      fontSize: '16px',
                      outline: 'none',
                      transition: 'border-color 0.2s',
                      marginLeft: '4px',
                    }}
                    countrySelectorStyleProps={{
                      buttonStyle: {
                        padding: '8px 12px',
                        border: validationErrors.phone ? '1px solid #ef4444' : '1px solid #d1d5db',
                        borderRadius: '8px 0 0 8px',
                        backgroundColor: '#f9fafb',
                        fontSize: '16px',
                        marginRight: '4px',
                      }
                    }}
                  />
                  {validationErrors.phone && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {validationErrors.phone}
                    </p>
                  )}
                </div>
              </div>

              {/* Password field for non-industrial users */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="password"
                    minLength={8}
                    title="Password must be at least 8 characters long"
                    className={`block w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      validationErrors.password ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Create a password"
                    value={formData.password}
                    onChange={(e) => {
                      setFormData({ ...formData, password: e.target.value });
                      validateField('password', e.target.value);
                    }}
                    disabled={isLoading}
                  />
                </div>
              </div>
            </>
          )}

          {/* Corporate fields for non-industrial users only */}
          {formData.accountType === 'corporate' && userType !== 'industrial' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Name
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Building2 className="h-5 w-5 text-gray-400" />
                  </div>
              <input
                type="text"
                required
                className={`block w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  validationErrors.companyName ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter company name"
                value={formData.companyName}
                onChange={(e) => {
                  setFormData({ ...formData, companyName: e.target.value });
                  validateField('companyName', e.target.value);
                }}
                disabled={isLoading}
              />
              {validationErrors.companyName && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {validationErrors.companyName}
                </p>
              )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Website URL
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Globe className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    inputMode="url"
                    autoComplete="url"
                    className={`block w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      validationErrors.websiteUrl ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="example.com or https://www.yourcompany.com"
                    value={formData.websiteUrl || ''}
                    onChange={(e) => {
                      setFormData({ ...formData, websiteUrl: e.target.value });
                      validateField('websiteUrl', e.target.value);
                    }}
                    disabled={isLoading}
                  />
                  {validationErrors.websiteUrl && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {validationErrors.websiteUrl}
                    </p>
                  )}
                </div>
              </div>

              {/* CR Number - Only for non-industrial users */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  CR Number
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FileText className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    className={`block w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      validationErrors.crNumber ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter CR number"
                    value={formData.crNumber}
                    onChange={(e) => {
                      setFormData({ ...formData, crNumber: e.target.value });
                      validateField('crNumber', e.target.value);
                    }}
                    disabled={isLoading}
                  />
                  {validationErrors.crNumber && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {validationErrors.crNumber}
                    </p>
                  )}
                </div>
              </div>
            </>
          )}



          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center text-red-700">
                <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
                {error}
              </div>
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              Your contact information helps us provide accurate energy design recommendations 
              and keeps you updated throughout the process.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row justify-between space-y-4 sm:space-y-0 sm:space-x-4">
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                className="bg-gray-100 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
              >
                Back
              </button>
            )}
            
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full bg-gradient-to-r from-blue-600 to-green-600 text-white py-3 px-4 rounded-lg hover:from-blue-700 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transform transition-all hover:scale-[1.02] active:scale-[0.98] font-medium text-lg shadow-lg disabled:opacity-50 disabled:cursor-not-allowed ${
                onBack ? 'sm:w-auto' : 'w-full'
              }`}
            >
              {isLoading ? 'Processing...' : 'Continue'}
            </button>
          </div>
        </form>
      </div>
      
      <div className="mt-12">
        <Footer />
      </div>
    </div>
  );
}